import { NullableMeasurementLike } from 'data/models/Measurement';
import { ConvertedMeasurement } from 'lib/ConvertedMeasurement';
import UOM from 'lib/UOM';
import { notNullish } from 'lib/notNullish';

import { CSVField, ExcelDateTimeFormat } from 'common/services/csvExport';

import { PierSettings } from 'data/models/PierSettings';
import { VesselSideDisplayName } from 'server/enums/BerthReservation';
import { LloydResult } from 'server/models/LloydResult';
import { Vessel } from 'data/models/Vessel';

import { PilotJobOrderViewModel } from './model';

export function CsvColumnNames(settings : PierSettings) {
    return {
        MovementType           : 'Movement Type',
        MovementStartEst       : 'Movement Start Date/Time (Est)',
        MovementStartActual    : 'Movement Start Date/Time (Actual)',
        MovementEndEst         : 'Movement End Date/Time (Est)',
        MovementEndActual      : 'Movement End Date/Time (Act)',
        VesselName             : 'Vessel Name',
        From                   : 'From',
        To                     : 'To',
        AgentCode              : 'Agent Code',
        AgencyName             : 'Agency Name',
        AttendingAgent         : 'Attending Agent ',
        AttendingAgentEmail    : 'Attending Agent Email',
        GrossTonnage           : 'Gross Tonnage',
        MovementLengthFeet     : 'Movement Length (feet)',
        MovementLengthMeters   : 'Movement Length (meters)',
        MovementBeamFeet       : 'Movement Beam  (feet)',
        MovementBeamMeters     : 'Movement Beam  (meters)',
        MovementDeepDraftFeet  : 'Movement Deep Draft (feet)',
        MovementDeepDraftMeters: 'Movement Deep Draft (meters)',
        MovementAirDraftFeet   : 'Movement Air Draft (feet)',
        MovementAirDraftMeters : 'Movement Air Draft (meters)',
        TugCode                : 'Tug Code',
        SideTo                 : 'Side To',
        PilotsAssigned         : 'Pilots Assigned',
        BowThrusterPresent     : 'Bow Thruster Present?',
        BowThrusterSize        : 'Bow Thruster Size',
        BowThrusterUoM         : 'Bow Thruster UoM',
        BowThrusterWorking     : 'Was bow thruster working?',
        VesselIMO              : 'IMO',
        VesselOfficialNumber   : 'Official Number',
        VesselCallSign         : 'CallSign',
        VesselVIN              : 'VIN',
        VesselOperatorLloyds   : 'Operator (Lloyds)',
        VesselDeadweightLloyds : 'Deadweight (Lloyds)',
        ShiptypeLevel2         : 'ShiptypeLevel2',
        ShiptypeLevel3         : 'ShiptypeLevel3',
        ShiptypeLevel4         : 'ShiptypeLevel4',
        ShiptypeLevel5         : 'ShiptypeLevel5',
        ShiptypeLevel5HullType : 'ShiptypeLevel5HullType',
        VesselTEULloyds        : 'TEU',
        VesselYearOfBuildLloyds: 'YearOfBuild',
        LastPort               : 'LastPort',
        NextPort               : 'NextPort'
    };
}

export function CSVColumns(settings : PierSettings) : CSVField<PilotJobOrderViewModel>[] {
    const colNames = CsvColumnNames(settings);
    return [{
        name: colNames.MovementType,
        formatter: item => item.MovementTypeText()
    }, {
        name: colNames.MovementStartEst,
        formatter: item => ExcelDateTimeFormat(item.Model.Start.Estimated())
    }, {
        name: colNames.MovementStartActual,
        formatter: item => item.Model.Start.Actual() ? ExcelDateTimeFormat(item.Model.Start.Actual()!) : ''
    }, {
        name: colNames.MovementEndEst,
        formatter: item => ExcelDateTimeFormat(item.Model.End.Estimated())
    }, {
        name: colNames.MovementEndActual,
        formatter: item => item.Model.End.Actual() ? ExcelDateTimeFormat(item.Model.End.Actual()!) : ''
    }, {
        name: colNames.VesselName,
        formatter: item => item.VesselNameText()
    }, {
        name: colNames.From,
        formatter: item => {
            const pt = item.Model.EffectiveFromPoint(),
                l = pt && pt.Location();
            return l && l.Name() || '';
        }
    }, {
        name: colNames.To,
        formatter: item => {
            const pt = item.Model.EffectiveToPoint(),
                l = pt && pt.Location();
            return l && l.Name() || '';
        }
    }, {
        name: colNames.AgentCode,
        formatter: item => item.AgencyCodeText()
    }, {
        name: colNames.AgencyName,
        formatter: item => item.Model.EffectivePortCall() && item.Model.EffectivePortCall()!.Agent() && item.Model.EffectivePortCall()!.Agent()!.Organization() && item.Model.EffectivePortCall()!.Agent()!.Organization()!.Name() || ''
    }, {
        name: colNames.AttendingAgent,
        formatter: item => item.Model.EffectivePortCall() && item.Model.EffectivePortCall()!.Agent() && item.Model.EffectivePortCall()!.Agent()!.FullName() || ''
    }, {
        name: colNames.AttendingAgentEmail,
        formatter: item => item.Model.EffectivePortCall() && item.Model.EffectivePortCall()!.Agent() && item.Model.EffectivePortCall()!.Agent()!.User() && item.Model.EffectivePortCall()!.Agent()!.User()!.Email() || ''
    }, {
        name: colNames.GrossTonnage,
        formatter: item => item.GrossTonnageText()
    }, {
        name: colNames.MovementLengthFeet,
        formatter: item => convert(item.Model.EffectiveLength, UOM.Foot, 2)
    }, {
        name: colNames.MovementLengthMeters,
        formatter: item => convert(item.Model.EffectiveLength, UOM.Meter, 2)
    }, {
        name: colNames.MovementBeamFeet,
        formatter: item => item.Model.EffectiveVessel() ? convert(item.Model.EffectiveVessel()!.Beam, UOM.Foot, 2) : ''
    }, {
        name: colNames.MovementBeamMeters,
        formatter: item => item.Model.EffectiveVessel() ? convert(item.Model.EffectiveVessel()!.Beam, UOM.Meter, 2) : ''
    }, {
        name: colNames.MovementDeepDraftFeet,
        formatter: item => convert(item.Model.EffectiveDeepDraft, UOM.Foot, 2)
    }, {
        name: colNames.MovementDeepDraftMeters,
        formatter: item => convert(item.Model.EffectiveDeepDraft, UOM.Meter, 2)
    }, {
        name: colNames.MovementAirDraftFeet,
        formatter: item => convert(item.Model.EffectiveAirDraft, UOM.Foot, 2)
    }, {
        name: colNames.MovementAirDraftMeters,
        formatter: item => convert(item.Model.EffectiveAirDraft, UOM.Meter, 2)
    }, {
        name: colNames.TugCode,
        formatter: item => item.Model.TugCompany() && item.Model.TugCompany()!.Code() || ''
    }, {
        name: colNames.SideTo,
        formatter: item => {
            const to = item.Model.EffectiveToPoint(),
                toRes = to && to.BerthReservation(), 
                from = item.Model.EffectiveFromPoint(),
                fromRes = from && from.BerthReservation();
            return toRes ? VesselSideDisplayName[toRes.SideTo()] :
                fromRes ? VesselSideDisplayName[fromRes.SideTo()] :
                '';
        }
    }, {
        name: colNames.PilotsAssigned,
        formatter: item => 
            // create list of pilot first initial last name, like 'E. Sanders, B. Cisco'
            item.Model.Pilots()
                .map(p => p.Pilot()?.User() ?? null)
                .filter(notNullish)
                .map(u => u.AbbreviatedName())
                .join(', ')
    }, {
        name: colNames.BowThrusterPresent,
        formatter: vesselField(v => yesNoNull(v.BowThruster.Present()))
    }, {
        name: colNames.BowThrusterSize,
        formatter: vesselField(v => v.BowThruster.Size.Num() === null ? null : v.BowThruster.Size.Num()!.toString())
    }, {
        name: colNames.BowThrusterUoM,
        formatter: vesselField(v => v.BowThruster.Size.UOM())
    }, {
        name: colNames.BowThrusterWorking,
        formatter: item => yesNoNull(item.Model.BowThrusterOperational())
    }, {
        name: colNames.VesselIMO,
        formatter: vesselField(v => v.IMO())
    }, {
        name: colNames.VesselOfficialNumber,
        formatter: lloydsResultField(l => l.OfficialNumber())
    }, {
        name: colNames.VesselCallSign,
        formatter: vesselField(v => v.CallSign())
    }, {
        name: colNames.VesselVIN,
        formatter: vesselField(v => v.VIN())
    }, {
        name: colNames.VesselOperatorLloyds,
        formatter: lloydsResultField(r => r.Operator())
    }, {
        name: colNames.VesselDeadweightLloyds,
        formatter: lloydsResultField(r => r.Deadweight())
    }, {
        name: colNames.ShiptypeLevel2,
        formatter: lloydsResultField(l => l.ShiptypeLevel2())
    }, {
        name: colNames.ShiptypeLevel3,
        formatter: lloydsResultField(l => l.ShiptypeLevel3())
    }, {
        name: colNames.ShiptypeLevel4,
        formatter: lloydsResultField(l => l.ShiptypeLevel4())
    }, {
        name: colNames.ShiptypeLevel5,
        formatter: lloydsResultField(l => l.ShiptypeLevel5())
    }, {
        name: colNames.ShiptypeLevel5HullType,
        formatter: lloydsResultField(l => l.ShiptypeLevel5HullType())
    }, {
        name: colNames.VesselTEULloyds,
        formatter: lloydsResultField(l => l.TEU())
    }, {
        name: colNames.VesselYearOfBuildLloyds,
        formatter: lloydsResultField(l => l.YearOfBuild())
    }, {
        name: colNames.LastPort,
        formatter: item => item.Model.EffectivePortCall() &&
                            item.Model.EffectivePortCall()!.LastPortOfCall() || ''
    }, {
        name: colNames.NextPort,
        formatter: item => item.Model.EffectivePortCall() &&
                            item.Model.EffectivePortCall()!.NextPortOfCall() || ''
    }
];
}

function vesselField(prop : (item : Vessel) => string | null) {
    return (item : PilotJobOrderViewModel) => {
        const v = item.Model.EffectiveVessel();
        return v && prop(v) || '';
    }
}

function lloydsResultField(prop : (item : LloydResult) => string | null) {
    return (item : PilotJobOrderViewModel) => {
        const v = item.Model.EffectiveVessel(),
            l = v && v.LloydServiceVessel(),
            r = l && l.VesselResult();
        return r && prop(r) || '';
    }
}

function yesNoNull(v : boolean | null) {
    return v === true ? 'Yes' : v === false ? 'No' : '';
}

function convert(v : NullableMeasurementLike, uom : string, precision : number) {
    return v.Num() === null ? '' : new ConvertedMeasurement(v, uom, precision).toString({ units: false })
}



// WEBPACK FOOTER //
// ./apps/pilots/dispatchdashboard/csvExport.ts